import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MagasinBadge from '../components/MagasinBadge';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import StockMagasinPicker from '../features/stock/StockMagasinPicker';
import { STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY } from '../features/stock/stockV2Ui';
import StockProductReviewModal from '../features/stock/StockProductReviewModal';
import { analyzeStockLines, inventoryRowToAnalyzeItem, registerImeisForArticle } from '../features/stock/stockProductClassify';
import { STOCK_TABS } from './_stockTabs';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportModal from '../components/CSVExportModal';
import { Plus, Trash2, Save, Store, Warehouse, Loader2, CheckCircle, AlertTriangle, X, RefreshCw, Camera, Printer, ArrowLeft, Upload, Download } from 'lucide-react';

const CURRENCY_FLAGS = { EUR: '\u{1F1EA}\u{1F1FA}', USD: '\u{1F1FA}\u{1F1F8}', BRL: '\u{1F1E7}\u{1F1F7}', COP: '\u{1F1E8}\u{1F1F4}' };

const emptyRow = () => ({
  key: Date.now() + Math.random(),
  reference: '', ean: '', designation: '',
  prix_achat: 0, prix_achat_original: 0, achat_currency: 'EUR',
  prix_vente: 0, quantite_add: 1, location: 'boutique',
  categorie: '', fournisseur: '', existing: null, status: 'new',
  imei_list: [''],
});

const StockInventoryPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [savedArticles, setSavedArticles] = useState([]);
  const [rates, setRates] = useState({ EUR: 1 });
  const [currencies, setCurrencies] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showCsvExport, setShowCsvExport] = useState(false);
  const [scanTargetRow, setScanTargetRow] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearchRow, setActiveSearchRow] = useState(null);
  const [activeSearchField, setActiveSearchField] = useState(null);
  // PhaseB (2026-02) — Sélecteur magasin obligatoire avant inventaire.
  // Pour super_admin → blocage si vide. Pour admin_magasin/technicien → pré-rempli
  // avec leur magasin assigné (read-only ou éditable selon scope back).
  const [inventoryMagasin, setInventoryMagasin] = useState(() => {
    try {
      return localStorage.getItem('pos_current_magasin_id') || (user?.default_magasin_id || '');
    } catch (_) { return user?.default_magasin_id || ''; }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLines, setReviewLines] = useState([]);
  const [reviewAnalyzing, setReviewAnalyzing] = useState(false);
  const [reviewRowKeys, setReviewRowKeys] = useState([]);
  const reanalyzeTimer = useRef(null);
  const refInputs = useRef({});
  const checkTimers = useRef({});
  const searchTimers = useRef({});

  useEffect(() => {
    api.get('/magasins').then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.magasins || []);
      const list = raw.filter(m => m.is_active !== false);
      setMagasinsList(list);
      // Auto-select default if there's only one
      if (!inventoryMagasin && list.length === 1) setInventoryMagasin(list[0].code);
    }).catch(() => setMagasinsList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    api.get('/exchange-rates').then(r => {
      setCurrencies(r.data.currencies || []);
      setRates(r.data.rates || { EUR: 1 });
    }).catch(() => {});
  }, [api]);

  const updateRow = useCallback((key, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.key !== key) return r;
      const updated = { ...r, [field]: value };
      if (field === 'prix_achat_original' || field === 'achat_currency') {
        const amt = field === 'prix_achat_original' ? value : r.prix_achat_original;
        const cur = field === 'achat_currency' ? value : r.achat_currency;
        const rate = rates[cur] || 1;
        updated.prix_achat = rate === 1 ? amt : +(amt / rate).toFixed(2);
      }
      return updated;
    }));
  }, [rates]);

  const searchStock = useCallback(async (key, query, field) => {
    if (!query || query.length < 2) { setSearchResults([]); setActiveSearchRow(null); return; }
    if (searchTimers.current[key]) clearTimeout(searchTimers.current[key]);
    searchTimers.current[key] = setTimeout(async () => {
      try {
        const r = await api.get(`/stock/search/${encodeURIComponent(query)}`, {
          params: { include_all_magasins: true },
        });
        if (r.data?.length > 0) {
          setSearchResults(r.data.slice(0, 8));
          setActiveSearchRow(key);
          setActiveSearchField(field);
        } else { setSearchResults([]); setActiveSearchRow(null); }
      } catch { setSearchResults([]); setActiveSearchRow(null); }
    }, 300);
  }, [api]);

  const selectSearchResult = (key, article) => {
    setRows(prev => prev.map(row => row.key === key ? {
      ...row, existing: article, status: 'existing',
      stock_magasins: [{ magasin_id: article.magasin_id || 'M1', quantite: article.quantite, quantite_depot: article.quantite_depot, article_id: article.id }],
      reference: article.reference || row.reference, ean: article.ean || row.ean,
      designation: article.designation || row.designation,
      prix_achat: article.prix_achat || row.prix_achat,
      prix_achat_original: article.prix_achat || row.prix_achat_original,
      prix_vente: article.prix_vente || row.prix_vente,
      categorie: article.categorie || row.categorie,
      fournisseur: article.fournisseur || row.fournisseur,
    } : row));
    setSearchResults([]); setActiveSearchRow(null);
  };

  const checkRef = useCallback(async (key, ref) => {
    if (!ref || ref.length < 2) {
      setRows(prev => prev.map(r => r.key === key ? { ...r, existing: null, status: 'new' } : r));
      return;
    }
    if (checkTimers.current[key]) clearTimeout(checkTimers.current[key]);
    checkTimers.current[key] = setTimeout(async () => {
      try {
        const r = await api.get(`/stock/by-reference?ref=${encodeURIComponent(ref)}`);
        if (r.data.found) {
          const a = r.data.article;
          setRows(prev => prev.map(row => row.key === key ? {
            ...row, existing: a, status: 'existing', stock_magasins: r.data.available_in_magasins || [],
            ean: a.ean || row.ean,
            designation: a.designation || row.designation, prix_achat: a.prix_achat || row.prix_achat,
            prix_achat_original: a.prix_achat || row.prix_achat_original, prix_vente: a.prix_vente || row.prix_vente,
            categorie: a.categorie || row.categorie, fournisseur: a.fournisseur || row.fournisseur,
          } : row));
        } else {
          setRows(prev => prev.map(r => r.key === key ? { ...r, existing: null, status: 'new' } : r));
        }
      } catch { setRows(prev => prev.map(r => r.key === key ? { ...r, existing: null } : r)); }
    }, 400);
  }, [api]);

  const handleBarcodeScan = useCallback(async (code) => {
    setShowScanner(false);
    try {
      const r = await api.get(`/stock/by-reference?ean=${encodeURIComponent(code)}`);
      if (scanTargetRow) {
        if (r.data.found) {
          const a = r.data.article;
          setRows(prev => prev.map(row => row.key === scanTargetRow ? {
            ...row, existing: a, status: 'existing', stock_magasins: r.data.available_in_magasins || [],
            reference: a.reference || row.reference, ean: a.ean || code,
            designation: a.designation || row.designation, prix_achat: a.prix_achat || row.prix_achat,
            prix_achat_original: a.prix_achat || row.prix_achat_original, prix_vente: a.prix_vente || row.prix_vente,
            categorie: a.categorie || row.categorie, fournisseur: a.fournisseur || row.fournisseur,
          } : row));
        } else {
          setRows(prev => prev.map(row => row.key === scanTargetRow ? { ...row, ean: code } : row));
        }
        setScanTargetRow(null);
      } else {
        const nr = emptyRow();
        if (r.data.found) {
          const a = r.data.article;
          const filled = { ...nr, existing: a, status: 'existing', stock_magasins: r.data.available_in_magasins || [],
            reference: a.reference, ean: a.ean || code,
            designation: a.designation || '', prix_achat: a.prix_achat || 0, prix_achat_original: a.prix_achat || 0,
            prix_vente: a.prix_vente || 0, categorie: a.categorie || '', fournisseur: a.fournisseur || '' };
          setRows(prev => prev.length === 1 && !prev[0].reference.trim() ? [filled] : [...prev, filled]);
        } else {
          setRows(prev => prev.length === 1 && !prev[0].reference.trim() ? [{ ...nr, ean: code }] : [...prev, { ...nr, ean: code }]);
        }
      }
    } catch {
      if (scanTargetRow) {
        setRows(prev => prev.map(row => row.key === scanTargetRow ? { ...row, ean: code } : row));
        setScanTargetRow(null);
      }
    }
  }, [api, scanTargetRow]);

  const openScanForRow = (key) => { setScanTargetRow(key); setShowScanner(true); };
  const openContinuousScan = () => { setScanTargetRow(null); setShowScanner(true); };

  const addRow = () => {
    const nr = emptyRow();
    setRows(prev => [...prev, nr]);
    setTimeout(() => refInputs.current[nr.key]?.focus(), 50);
  };

  const removeRow = (key) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter(r => r.key !== key));
  };

  // Phase 5.4a-csv-conflicts — the modal now drives the whole import flow
  // (preview → strategy → apply). We just receive the final result for our
  // own banner / refresh.
  const handleCsvImport = async (result) => {
    if (!result) return;
    setResult({
      created: result.created || 0,
      updated: result.updated || 0,
      skipped: result.skipped || 0,
      merged: result.merged || 0,
      errors: result.errors || [],
    });
    setSavedArticles(result.articles || []);
  };

  const handleSubmit = async () => {
    const valid = rows.filter(r => r.reference.trim() && r.quantite_add > 0);
    if (!valid.length) return;
    if (!inventoryMagasin) {
      setResult({ errors: [t('stockInv.error.pickMagasin')] });
      return;
    }
    setReviewAnalyzing(true);
    setReviewOpen(true);
    setReviewRowKeys(valid.map((r) => r.key));
    try {
      const data = await analyzeStockLines(api, valid.map(inventoryRowToAnalyzeItem));
      setReviewLines(data.lines || []);
      const needsReview = !data.can_save_all || (data.phone_count || 0) > 0 || (data.issue_count || 0) > 0;
      if (!needsReview) {
        setReviewOpen(false);
        await executeBulkSave(valid, data.lines || []);
      }
    } catch (e) {
      setReviewOpen(false);
      setResult({ errors: [e.response?.data?.detail || t('stockInv.error.analyzeFailed')] });
    } finally {
      setReviewAnalyzing(false);
    }
  };

  const executeBulkSave = async (valid, analysisLines = []) => {
    setSaving(true);
    setResult(null);
    setSavedArticles([]);
    const analysisByIndex = Object.fromEntries((analysisLines || []).map((ln, i) => [i, ln]));
    try {
      const res = await api.post('/stock/bulk-add', {
        magasin_id: inventoryMagasin,
        items: valid.map((r, idx) => {
          const ln = analysisByIndex[idx] || {};
          return {
            reference: r.reference,
            ean: r.ean,
            designation: ln.designation || r.designation,
            categorie: ln.categorie || r.categorie || ln.suggested_categorie || '',
            prix_achat: r.prix_achat,
            prix_vente: r.prix_vente,
            quantite_add: r.quantite_add,
            location: r.location,
            fournisseur: r.fournisseur,
            magasin_id: inventoryMagasin,
          };
        }),
      }, { headers: { 'X-Magasin-Session-Id': inventoryMagasin } });
      setResult(res.data);
      if (res.data.articles?.length) setSavedArticles(res.data.articles);

      for (let i = 0; i < valid.length; i += 1) {
        const ln = analysisByIndex[i];
        if (!ln?.needs_imei) continue;
        const imeis = (ln.imei_list || []).filter((x) => String(x || '').replace(/\D/g, '').length >= 14);
        if (imeis.length) {
          await registerImeisForArticle(api, valid[i].reference, imeis, inventoryMagasin);
        }
      }

      if (!res.data.errors?.length) setRows([emptyRow()]);
      setReviewOpen(false);
    } catch (e) {
      setResult({ errors: [e.response?.data?.detail || t('stockInv.error.server')] });
    }
    setSaving(false);
  };

  const handleReviewConfirm = async () => {
    const valid = rows.filter((r) => reviewRowKeys.includes(r.key));
    if (!reviewLines.every((ln) => ln.can_save !== false)) return;
    await executeBulkSave(valid, reviewLines);
  };

  // Saisie d'un IMEI dans la revue : ne JAMAIS relancer une pré-analyse asynchrone.
  // La pré-analyse (réseau) capturait l'IMEI à l'instant T puis, en revenant,
  // écrasait `reviewLines` via setReviewLines — effaçant les chiffres tapés pendant
  // l'aller-retour (le champ « s'effaçait » pendant la saisie). Un IMEI ne change
  // jamais la classification : on recalcule donc ses anomalies LOCALEMENT et de façon
  // SYNCHRONE (mêmes règles que le backend _build_issues), sans aucun appel réseau.
  const recomputeImeiIssues = (line) => {
    if (!line.needs_imei) return line;
    const qty = Math.max(1, Number(line.quantite) || 1);
    // Mêmes règles EXACTES que le backend (normalize_imei + analyze_line/_build_issues) :
    // un IMEI ne compte que s'il fait 14 à 20 chiffres (après suppression des non-chiffres) ;
    // sinon il est « manquant ». Le backend n'émet jamais 'imei_invalid' depuis analyze
    // (normalize_imei a déjà écarté les longueurs hors 14-20). On ne relâche donc rien.
    const filled = (line.imei_list || [])
      .map((x) => String(x || '').replace(/\D/g, ''))
      .filter((d) => d.length >= 14 && d.length <= 20);
    const missing = Math.max(0, qty - filled.length);
    const kept = (line.issues || []).filter((iss) => iss.code !== 'imei_missing' && iss.code !== 'imei_invalid');
    const imeiIssues = [];
    if (missing > 0) imeiIssues.push({ code: 'imei_missing', field: 'imei', message: `IMEI obligatoire — ${missing} manquant(s) sur ${qty} téléphone(s)` });
    const issues = [...kept, ...imeiIssues];
    const blocking = issues.some((iss) => ['imei_missing', 'imei_invalid', 'designation_missing', 'reference_missing'].includes(iss.code));
    return { ...line, imei_filled: filled.length, issues, can_save: !blocking };
  };

  const handleReviewLineChange = (index, patch) => {
    const imeiOnly = ('imei_list' in patch) && !('designation' in patch) && !('categorie' in patch);
    setReviewLines((prev) => prev.map((ln) => {
      if (ln.index !== index) return ln;
      const merged = { ...ln, ...patch };
      return imeiOnly ? recomputeImeiIssues(merged) : merged;
    }));
    const key = reviewRowKeys[index];
    if (key) {
      setRows((prev) => prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        if (patch.imei_list) next.imei_list = patch.imei_list;
        return next;
      }));
    }
    // Seuls désignation/catégorie changent la classification -> pré-analyse (rare).
    // Un IMEI ne relance jamais d'appel réseau (cf. correctif ci-dessus).
    if (imeiOnly) return;
    if (reanalyzeTimer.current) clearTimeout(reanalyzeTimer.current);
    reanalyzeTimer.current = setTimeout(async () => {
      const valid = rows.filter((r) => reviewRowKeys.includes(r.key));
      const merged = valid.map((r, i) => {
        if (i !== index) return inventoryRowToAnalyzeItem(r);
        return inventoryRowToAnalyzeItem({ ...r, ...patch });
      });
      try {
        const data = await analyzeStockLines(api, merged);
        // Conserver les IMEI déjà saisis : ne prendre du backend que la classification/anomalies.
        setReviewLines((prev) => (data.lines || []).map((ln) => {
          const cur = prev.find((p) => p.index === ln.index);
          return cur && cur.imei_list ? { ...ln, imei_list: cur.imei_list } : ln;
        }));
      } catch { /* keep current */ }
    }, 450);
  };

  const goToLabels = () => {
    const preselected = savedArticles.map(a => ({
      id: a.id,
      qty: a.added_qty || 1,
    }));
    navigate('/etiquettes', { state: { preselected, stockFilter: 'boutique' } });
  };

  const handleKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchResults([]); setActiveSearchRow(null);
      const idx = rows.findIndex(r => r.key === key);
      if (idx === rows.length - 1) addRow();
      else { const next = rows[idx + 1]; refInputs.current[next.key]?.focus(); }
    }
  };

  const resetSummary = () => {
    setSavedArticles([]);
    setResult(null);
  };

  const handleInventoryMagasinChange = (code) => {
    setInventoryMagasin(code);
    if (code !== 'M1') {
      setRows(prev => prev.map(r => (r.location === 'depot' ? { ...r, location: 'boutique' } : r)));
    }
  };

  const goToLabelsFromDraft = () => {
    if (!validRows.length) return;
    const preselected = validRows.map((r, i) => ({
      id: r.existing?.id,
      reference: r.reference,
      qty: r.quantite_add || 1,
    }));
    navigate('/etiquettes', { state: { preselected, stockFilter: 'boutique' } });
  };

  const validRows = rows.filter(r => r.reference.trim() && r.quantite_add > 0);
  const totalItems = validRows.reduce((s, r) => s + r.quantite_add, 0);
  const totalValue = validRows.reduce((s, r) => s + (r.prix_achat * r.quantite_add), 0);
  const newCount = validRows.filter(r => !r.existing).length;
  const existCount = validRows.filter(r => r.existing).length;

  const SearchDropdown = ({ rowKey, field }) => {
    if (activeSearchRow !== rowKey || activeSearchField !== field || !searchResults.length) return null;
    return (
      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-[#151820] border border-white/10 rounded-xl shadow-2xl max-h-48 overflow-y-auto" data-testid={`search-dropdown-${field}`}>
        {searchResults.map((a, i) => (
          <button key={`${a.id || i}-${a.magasin_id || 'M1'}`} type="button" onClick={() => selectSearchResult(rowKey, a)}
            className="w-full px-3 py-2 text-left hover:bg-red-950/20 border-b border-white/5 last:border-0 transition-colors"
            data-testid={`search-result-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-white font-medium truncate">{a.designation}</div>
              <MagasinBadge code={a.magasin_id || 'M1'} size="sm" />
            </div>
            <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 mt-0.5">
              <span>{t('stockInv.dropdown.refLabel')} {a.reference}</span>
              {a.ean && <span>EAN: {a.ean}</span>}
              <span className="text-green-400">{formatCurrency(a.prix_vente || 0)}</span>
              <span>{t('stockInv.short.boutique')}:{a.quantite || 0} {t('stockInv.short.depot')}:{a.quantite_depot || 0}</span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const StockMagasinHint = ({ row }) => {
    if (!row.existing) return null;
    const magasins = row.stock_magasins?.length
      ? row.stock_magasins
      : [{ magasin_id: row.existing.magasin_id || 'M1', quantite: row.existing.quantite, quantite_depot: row.existing.quantite_depot }];
    return (
      <div className="flex flex-wrap gap-1 mt-1" data-testid={`inv-row-magasin-${row.reference}`}>
        {magasins.map((m) => (
          <span key={`${m.article_id || m.magasin_id}`} className="inline-flex items-center gap-1">
            <MagasinBadge code={m.magasin_id || 'M1'} size="sm" />
            <span className="text-[10px] text-gray-500">{t('stockInv.short.boutique')}:{m.quantite || 0} {t('stockInv.short.depot')}:{m.quantite_depot || 0}</span>
          </span>
        ))}
      </div>
    );
  };

  // Summary view after save
  if (savedArticles.length > 0) {
    const createdArticles = savedArticles.filter(a => a.action === 'created');
    const updatedArticles = savedArticles.filter(a => a.action === 'updated');
    const totalAdded = savedArticles.reduce((s, a) => s + (a.added_qty || 0), 0);
    const totalPurchaseValue = savedArticles.reduce((s, a) => s + (a.prix_achat || 0) * (a.added_qty || 0), 0);

    return (
      <Layout api={api} user={user}>
        <div className="max-w-6xl mx-auto space-y-4 fade-in" data-testid="inventory-summary">
          <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
          <StockSatelliteHeader title={t('stockInv.summary.title')} subtitle={t('stockInv.summary.subtitle', { count: savedArticles.length })} backTestId="inventory-summary-back" />
          <div className="flex items-center justify-end flex-wrap gap-2">
            <button type="button" onClick={goToLabels} className={STOCK_BTN_SECONDARY} data-testid="print-labels-btn">
              <Printer className="w-4 h-4" /> {t('stockInv.btn.printLabels')}
            </button>
            <button type="button" onClick={resetSummary} className={STOCK_BTN_PRIMARY} data-testid="new-inventory-btn">
              <ArrowLeft className="w-4 h-4" /> {t('stockInv.btn.newInventory')}
            </button>
          </div>

          <div className={`${STOCK_PANEL} overflow-x-auto`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-white/10">
                  <th className="px-3 py-3 text-left">#</th>
                  <th className="px-3 py-3 text-left">{t('stockInv.col.status')}</th>
                  <th className="px-3 py-3 text-left">{t('stockInv.col.reference')}</th>
                  <th className="px-3 py-3 text-left">{t('stockInv.col.designation')}</th>
                  <th className="px-3 py-3 text-right">{t('stockInv.col.pAchat')}</th>
                  <th className="px-3 py-3 text-right">{t('stockInv.col.pVente')}</th>
                  <th className="px-3 py-3 text-center">{t('stockInv.col.lieu')}</th>
                  <th className="px-3 py-3 text-center">{t('stockInv.col.qteAjoutee')}</th>
                  <th className="px-3 py-3 text-center">{t('stockInv.col.stockTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {savedArticles.map((a, idx) => (
                  <tr key={a.id || idx} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`summary-row-${idx}`}>
                    <td className="px-3 py-2.5 text-gray-600">{idx + 1}</td>
                    <td className="px-3 py-2.5">
                      {a.action === 'created' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                          <Plus className="w-3 h-3" /> {t('stockInv.summary.statusNew')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs font-medium">
                          <RefreshCw className="w-3 h-3" /> {t('stockInv.summary.statusUpdated')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-white font-mono text-xs">{a.reference}</td>
                    <td className="px-3 py-2.5 text-white">{a.designation}</td>
                    <td className="px-3 py-2.5 text-right text-gray-400">{formatCurrency(a.prix_achat || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-400 font-semibold">{formatCurrency(a.prix_vente || 0)}</td>
                    <td className="px-3 py-2.5 text-center">
                      {a.location === 'depot' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-300"><Warehouse className="w-3 h-3" /> {t('stockInv.location.depot')}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><Store className="w-3 h-3" /> {t('stockInv.location.boutique')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-red-950/40 text-red-300 font-bold text-sm border border-red-500/20">+{a.added_qty}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center text-white font-semibold">
                      {a.location === 'depot' ? (a.quantite_depot || 0) : (a.quantite || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Errors if any */}
          {result?.errors?.length > 0 && (
            <div className="p-3 rounded-xl border bg-amber-500/10 border-amber-500/30">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-amber-400 font-semibold">{t('stockInv.summary.errorsTitle')}</span></div>
              {result.errors.map((e, i) => <div key={i} className="text-sm text-amber-300">{e}</div>)}
            </div>
          )}

          <div className={`${STOCK_PANEL} p-4 flex items-center justify-between flex-wrap gap-3`}>
            <div className="text-sm text-gray-400">
              {createdArticles.length > 0 && <span className="text-emerald-400">{t('stockInv.summary.created', { count: createdArticles.length })} </span>}
              {updatedArticles.length > 0 && <span className="text-gray-300">{t('stockInv.summary.updated', { count: updatedArticles.length })} </span>}
              · {t('stockInv.summary.pieces', { count: totalAdded })} · {formatCurrency(totalPurchaseValue)}
            </div>
            <button type="button" onClick={goToLabels} className={STOCK_BTN_PRIMARY} data-testid="print-labels-btn-bottom">
              <Printer className="w-5 h-5" /> {t('stockInv.btn.printCount', { count: savedArticles.length })}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout api={api} user={user}>
      <div className="max-w-7xl mx-auto space-y-4 fade-in" data-testid="inventory-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title={t('stockInv.header.title')}
          subtitle={inventoryMagasin ? t('stockInv.header.subtitleMagasin', { magasin: inventoryMagasin }) : t('stockInv.header.subtitleNoMagasin')}
          backTestId="inventory-back"
        />

        <div className={`${STOCK_PANEL} p-4 space-y-4`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <StockMagasinPicker
              magasinsList={magasinsList}
              value={inventoryMagasin}
              onChange={handleInventoryMagasinChange}
              required
              compact
            />
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={addRow} className={STOCK_BTN_PRIMARY} data-testid="add-row-btn">
                <Plus className="w-4 h-4" /> {t('stockInv.btn.addRow')}
              </button>
              <button type="button" onClick={openContinuousScan} className={STOCK_BTN_SECONDARY} data-testid="continuous-scan-btn">
                <Camera className="w-4 h-4" /> {t('stockInv.btn.scan')}
              </button>
              <button type="button" onClick={() => setShowCsvImport(true)} className={STOCK_BTN_SECONDARY} data-testid="csv-import-open-btn">
                <Upload className="w-4 h-4" /> {t('stockInv.btn.csvImport')}
              </button>
              <button type="button" onClick={() => setShowCsvExport(true)} className={STOCK_BTN_SECONDARY} data-testid="csv-export-open-btn">
                <Download className="w-4 h-4" /> {t('stockInv.btn.csvExport')}
              </button>
              <button type="button" onClick={goToLabelsFromDraft} disabled={!validRows.length} className={STOCK_BTN_SECONDARY} data-testid="inventory-print-labels-btn">
                <Printer className="w-4 h-4" /> {t('stockInv.btn.printLabels')}
              </button>
            </div>
          </div>
          {!inventoryMagasin && (
            <p className="text-xs text-amber-300/90 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              {t('stockInv.warn.pickMagasin')}
            </p>
          )}
        </div>

        {result && !savedArticles.length && (
          <div className={`p-3 rounded-xl border flex items-start gap-2 ${result.errors?.length ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
            {result.errors?.length ? <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" /> : <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />}
            <div className="text-sm">
              {result.created > 0 && <span className="text-green-400 font-semibold">{t('stockInv.summary.created', { count: result.created })} </span>}
              {result.updated > 0 && <span className="text-blue-400 font-semibold">{t('stockInv.summary.updated', { count: result.updated })} </span>}
              {result.errors?.map((e, i) => <div key={i} className="text-amber-400">{e}</div>)}
            </div>
            <button onClick={() => setResult(null)} className="ml-auto text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* PhaseB-bugfix — Conflits cross-magasin détectés par le back. La
            ligne saisie a été créée dans le magasin cible mais l'article existe
            DÉJÀ dans un autre magasin (ou en legacy null-magasin). Carlos sait
            ainsi si une duplication d'inventaire est volontaire ou si un
            transfert serait plus indiqué. */}
        {result?.cross_magasin_conflicts?.length > 0 && (
          <div
            className="p-3 rounded-xl border bg-amber-500/10 border-amber-500/40 flex items-start gap-3"
            data-testid="inventory-cross-magasin-warning"
          >
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <div className="font-bold text-amber-200 mb-1">
                ⚠️ {t('stockInv.cross.title', { count: result.cross_magasin_conflicts.length })}
              </div>
              <div className="text-xs text-amber-100/80 mb-2">
                {t('stockInv.cross.explain')} <span className="font-mono">/stock/mouvements</span> {t('stockInv.cross.explainTransfer')}
              </div>
              <ul className="space-y-1">
                {result.cross_magasin_conflicts.map((c, i) => (
                  <li
                    key={i}
                    className="text-xs text-amber-100 font-mono bg-black/20 rounded px-2 py-1 flex items-center gap-2 flex-wrap"
                    data-testid={`inventory-cross-conflict-${i}`}
                  >
                    <span className="font-bold">{c.reference}</span>
                    {c.ean && <span className="text-gray-400">EAN {c.ean}</span>}
                    <span className="text-emerald-300">→ {t('stockInv.cross.createdIn', { magasin: c.target_magasin })}</span>
                    <span className="text-amber-300">
                      ({t('stockInv.cross.existedIn')} {c.existing_magasin || <span className="italic">{t('stockInv.cross.noMagasin')}</span>}{' '}
                      {t('stockInv.cross.qty')} {c.existing_qty_boutique + c.existing_qty_depot})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={() => setResult(prev => prev ? { ...prev, cross_magasin_conflicts: [] } : null)} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className={`${STOCK_PANEL} overflow-visible`}>
          <div style={{ overflow: 'visible' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-white/10">
                  <th className="px-2 py-3 text-left w-8">#</th>
                  <th className="px-2 py-3 text-left w-[72px]">{t('stockInv.col.status')}</th>
                  <th className="px-2 py-3 text-left w-[110px]">{t('stockInv.col.magasin')}</th>
                  <th className="px-2 py-3 text-left min-w-[140px]">{t('stockInv.col.reference')}</th>
                  <th className="px-2 py-3 text-left w-[130px]">EAN</th>
                  <th className="px-2 py-3 text-left min-w-[200px]">{t('stockInv.col.designation')}</th>
                  <th className="px-2 py-3 text-left min-w-[150px]">{t('stockInv.col.prixAchat')}</th>
                  <th className="px-2 py-3 text-left w-[90px]">{t('stockInv.col.prixVente')}</th>
                  <th className="px-2 py-3 text-center w-[100px]">{t('stockInv.col.emplacement')}</th>
                  <th className="px-2 py-3 text-center w-[60px]">{t('stockInv.col.qte')}</th>
                  <th className="px-2 py-3 text-left min-w-[90px]">{t('stockInv.col.fournisseur')}</th>
                  <th className="px-2 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className={`border-b border-white/5 transition-colors ${row.existing ? 'bg-white/[0.03]' : ''}`}
                    data-testid={`inventory-row-${idx}`}>
                    <td className="px-2 py-2 text-gray-600 text-xs">{idx + 1}</td>
                    <td className="px-2 py-2">
                      {row.existing ? (
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-950/40 text-blue-300 border border-blue-500/30">{t('stockInv.badge.existing')}</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-950/40 text-emerald-300 border border-emerald-500/30">{t('stockInv.badge.new')}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <StockMagasinHint row={row} />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1 items-center">
                        <button type="button" onClick={() => openScanForRow(row.key)}
                          className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors shrink-0"
                          title={t('stockInv.btn.scan')} data-testid={`inv-scan-${idx}`}>
                          <Camera className="w-3.5 h-3.5" />
                        </button>
                        <div className="relative flex-1">
                          <input ref={el => refInputs.current[row.key] = el}
                            value={row.reference}
                            onChange={e => { const val = e.target.value; updateRow(row.key, 'reference', val); checkRef(row.key, val); searchStock(row.key, val, 'ref'); }}
                            onKeyDown={e => handleKeyDown(e, row.key)}
                            onBlur={() => setTimeout(() => { if (activeSearchRow === row.key && activeSearchField === 'ref') { setSearchResults([]); setActiveSearchRow(null); } }, 200)}
                            placeholder={t('stockInv.placeholder.refEan')}
                            className={`input-dark w-full py-1.5 px-2 text-sm ${row.existing ? 'border-red-500/30' : ''}`}
                            data-testid={`inv-ref-${idx}`} />
                          {row.existing && <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-red-500"></span>}
                          <SearchDropdown rowKey={row.key} field="ref" />
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input value={row.ean}
                        onChange={e => updateRow(row.key, 'ean', e.target.value)}
                        placeholder={t('stockInv.placeholder.ean13')}
                        className={`input-dark w-full py-1.5 px-2 text-sm font-mono ${row.ean && row.ean.length === 13 ? 'border-green-500/40' : ''}`}
                        maxLength={13}
                        data-testid={`inv-ean-${idx}`} />
                    </td>
                    <td className="px-2 py-2">
                      <div className="relative">
                        <input value={row.designation}
                          onChange={e => { const val = e.target.value; updateRow(row.key, 'designation', val); searchStock(row.key, val, 'desig'); }}
                          onBlur={() => setTimeout(() => { if (activeSearchRow === row.key && activeSearchField === 'desig') { setSearchResults([]); setActiveSearchRow(null); } }, 200)}
                          placeholder={t('stockInv.placeholder.productName')}
                          className="input-dark w-full py-1.5 px-2 text-sm"
                          data-testid={`inv-desig-${idx}`} />
                        <SearchDropdown rowKey={row.key} field="desig" />
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        <select value={row.achat_currency}
                          onChange={e => updateRow(row.key, 'achat_currency', e.target.value)}
                          className="input-dark py-1.5 px-1 text-xs w-[70px]" data-testid={`inv-currency-${idx}`}>
                          {currencies.length > 0 ? currencies.map(c => (
                            <option key={c.code} value={c.code}>{CURRENCY_FLAGS[c.code]} {c.code}</option>
                          )) : <option value="EUR">{CURRENCY_FLAGS.EUR} EUR</option>}
                        </select>
                        <div className="relative flex-1">
                          <input type="number" value={row.prix_achat_original}
                            onChange={e => updateRow(row.key, 'prix_achat_original', parseFloat(e.target.value) || 0)}
                            className="input-dark w-full py-1.5 px-2 text-sm text-center" min="0" step="0.01"
                            data-testid={`inv-prix-achat-${idx}`} />
                          {row.achat_currency !== 'EUR' && row.prix_achat_original > 0 && (
                            <div className="absolute -bottom-4 left-0 right-0 flex items-center justify-center gap-0.5">
                              <RefreshCw className="w-2.5 h-2.5 text-green-400" />
                              <span className="text-[10px] text-green-400 font-medium">{formatCurrency(row.prix_achat)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={row.prix_vente}
                        onChange={e => updateRow(row.key, 'prix_vente', parseFloat(e.target.value) || 0)}
                        className="input-dark w-full py-1.5 px-2 text-sm text-center" min="0" step="0.01"
                        data-testid={`inv-prix-vente-${idx}`} />
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={row.location}
                        onChange={(e) => updateRow(row.key, 'location', e.target.value)}
                        className="input-dark w-full py-1.5 px-1 text-xs"
                        data-testid={`inv-loc-select-${idx}`}
                      >
                        <option value="boutique">{t('stockInv.location.boutique')}</option>
                        {inventoryMagasin === 'M1' && <option value="depot">{t('stockInv.location.depot')}</option>}
                      </select>
                      {row.existing && (
                        <div className="text-[10px] text-center mt-1 text-gray-500">{t('stockInv.short.boutique')}:{row.existing.quantite || 0} {t('stockInv.short.depot')}:{row.existing.quantite_depot || 0}</div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" value={row.quantite_add}
                        onChange={e => updateRow(row.key, 'quantite_add', parseInt(e.target.value) || 0)}
                        className="input-dark w-full py-1.5 px-2 text-sm text-center font-bold" min="0"
                        data-testid={`inv-qty-${idx}`} />
                    </td>
                    <td className="px-2 py-2">
                      <input value={row.fournisseur}
                        onChange={e => updateRow(row.key, 'fournisseur', e.target.value)}
                        placeholder={t('stockInv.placeholder.fournisseur')}
                        className="input-dark w-full py-1.5 px-2 text-sm" />
                    </td>
                    <td className="px-2 py-2">
                      <button onClick={() => removeRow(row.key)}
                        className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        disabled={rows.length <= 1} data-testid={`inv-delete-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`${STOCK_PANEL} p-4 flex items-center justify-between flex-wrap gap-4`}>
          <div className="text-sm text-gray-500">
            <span className="text-white font-semibold">{rows.length}</span> {t('stockInv.footer.rows')}
            {' · '}
            <span className="text-white font-semibold">{validRows.length}</span> {t('stockInv.footer.toSave')}
            {existCount > 0 && <span className="text-gray-400 ml-2">{t('stockInv.footer.existingCount', { count: existCount })}</span>}
            {newCount > 0 && <span className="text-emerald-400/90 ml-1">{t('stockInv.footer.newCount', { count: newCount })}</span>}
            {' · '}
            <span className="text-gray-400">{t('stockInv.footer.piecesAndValue', { count: totalItems, value: formatCurrency(totalValue) })}</span>
          </div>
          <button type="button" onClick={handleSubmit} disabled={saving || !validRows.length || !inventoryMagasin}
            className={`${STOCK_BTN_PRIMARY} px-6 py-3`}
            data-testid="bulk-save-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? t('stockInv.btn.saving') : t('stockInv.btn.saveAll', { count: validRows.length })}
          </button>
        </div>

        <p className="text-xs text-gray-600 text-center">
          {t('stockInv.hint.enterBefore')} <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400 font-mono">{t('stockInv.hint.enterKey')}</kbd> {t('stockInv.hint.enterAfter')}
        </p>
      </div>

      <BarcodeScannerModal isOpen={showScanner} onClose={() => { setShowScanner(false); setScanTargetRow(null); }} onScan={handleBarcodeScan} />
      <StockProductReviewModal
        open={reviewOpen}
        title={t('stockInv.review.title')}
        subtitle={t('stockInv.review.subtitle', { count: reviewLines.length, magasin: inventoryMagasin || '—' })}
        lines={reviewLines}
        analyzing={reviewAnalyzing}
        saving={saving}
        onClose={() => { if (!saving) setReviewOpen(false); }}
        onConfirm={handleReviewConfirm}
        onLineChange={handleReviewLineChange}
      />
      <CSVImportModal
        open={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        onConfirm={handleCsvImport}
      />
      <CSVExportModal
        open={showCsvExport}
        onClose={() => setShowCsvExport(false)}
        api={api}
      />
    </Layout>
  );
};

export default StockInventoryPage;
